// ==================== FILE 5: src/components/Banner/index.js (Updated) ====================
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './index.css';

const ModernBanner = ({ config, stores = [], onSingleStoreQuickFlow }) => {
  const { banner } = config;
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleBookTableClick = (e) => {
    e.preventDefault();
    if (stores.length === 1 && onSingleStoreQuickFlow) {
      onSingleStoreQuickFlow();
    } else {
      navigate('/restaurants');
    }
  };

  const desktopImage = config.backgroundImages.banner;
  // Optional dedicated mobile crop — not part of the current banner
  // schema, only used if the API actually provides one. Same opt-in
  // pattern Template 16's banner already uses for the same purpose.
  const mobileImage = banner.mobile_image || banner.mobile_image_url || null;

  return (
    <div id="banner" className={`modern-hero ${isVisible ? 'modern-hero--visible' : ''}`}>
      <div className="modern-hero__frame">
        {/* Food photography fills the whole banner — this IS the hero,
            not a side illustration next to a text column. */}
        <div className="modern-hero__media">
          <picture>
            {mobileImage && <source media="(max-width: 767px)" srcSet={mobileImage} />}
            <img
              src={desktopImage}
              alt={banner.heading}
              className="modern-hero__media-img"
            />
          </picture>
        </div>
        <div className="modern-hero__scrim" />

        <div className="modern-hero__content">
          {banner.sub_heading && (
            <span className="modern-hero__badge">{banner.sub_heading}</span>
          )}

          <h1 className="modern-hero__title">
            {banner.heading}
          </h1>

          {banner.description && (
            <p className="modern-hero__desc">{banner.description}</p>
          )}

          <div className="modern-hero__actions">
            <a
              href="/restaurants"
              className="modern-hero__btn"
              onClick={handleBookTableClick}
            >
              <span>{banner.button_text}</span>
              <i className="fa fa-arrow-right" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const ClassicBanner = ({ config, stores = [], onSingleStoreQuickFlow }) => {
  const [isVisible, setIsVisible] = useState(false);
  const { banner } = config;
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleOrderClick = (e) => {
    e.preventDefault();
    if (stores.length === 1 && onSingleStoreQuickFlow) {
      onSingleStoreQuickFlow();
    } else {
      navigate('/restaurants');
    }
  };

  const bannerBgImage = config.backgroundImages?.banner || '';

  return (
    <div id="banner" className={`banner-hero ${isVisible ? 'banner-hero--visible' : ''}`}>
      <div className="banner-hero__stripe"></div>

      <div className="banner-hero__inner">
        <div className="banner-hero__text">
          <h2 className="banner-hero__heading">
            {banner.heading}
          </h2>
          <p className="banner-hero__desc">
            {banner.description}
          </p>
          <div className="banner-hero__cta">
            <a
              href="/restaurants"
              className="banner-hero__btn"
              onClick={handleOrderClick}
            >
              {banner.button_text}
            </a>
          </div>
          <div className="banner-hero__social">
            {/* Social links removed to be strictly dynamic with current schema */}
          </div>
        </div>

        <div className="banner-hero__plate">
          <div className="banner-hero__circle">
            <img
              src={bannerBgImage}
              alt={banner.heading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const TraditionalBanner = ({ config, stores = [], onSingleStoreQuickFlow }) => {
  const { banner } = config;
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  const handleOrderClick = (e) => {
    e.preventDefault();
    if (stores.length === 1 && onSingleStoreQuickFlow) {
      onSingleStoreQuickFlow();
    } else {
      navigate('/restaurants');
    }
  };

  const bannerBgImage = config.backgroundImages?.banner || '';

  return (
    <div id="banner" className={`trad-banner ${isVisible ? 'trad-banner--visible' : ''}`}>
      <div className="trad-banner__container">
        {/* Left Side: Text Content */}
        <div className="trad-banner__content">
          <div className="trad-banner__badge">
            <span className="trad-banner__badge-icon">✧</span>
            {banner.sub_heading || 'Heritage Sweets & Dining'}
            <span className="trad-banner__badge-icon">✧</span>
          </div>
          <h1 className="trad-banner__title">{banner.heading}</h1>
          <p className="trad-banner__desc">{banner.description}</p>
          <a
            href="/restaurants"
            className="trad-banner__btn"
            onClick={handleOrderClick}
          >
            {banner.button_text}
          </a>
        </div>

        {/* Right Side: Image Showcase */}
        <div className="trad-banner__visual">
          <div className="trad-banner__arch-wrapper">
            <div className="trad-banner__arch-border"></div>
            <img
              src={bannerBgImage}
              alt={banner.heading}
              className="trad-banner__arch-img"
            />
          </div>
          {/* Floating ornate badges */}
        </div>
      </div>
    </div>
  );
};

const ApparelBanner = ({ config, stores = [], onSingleStoreQuickFlow }) => {
  const { banner } = config;
  const navigate = useNavigate();

  const handleShopClick = (e) => {
    e.preventDefault();
    if (stores.length === 1 && onSingleStoreQuickFlow) {
      onSingleStoreQuickFlow();
    } else {
      navigate('/restaurants');
    }
  };

  return (
    <div id="banner" className="apparel-banner">
      {/* Abstract Background Shapes */}
      <div className="apparel-shape apparel-shape-circle"></div>
      <div className="apparel-shape apparel-shape-line"></div>
      <div className="apparel-shape apparel-shape-line-2"></div>

      <div className="apparel-banner__layout">

        {/* Left Column Spacer */}
        <div className="apparel-banner__side-nav">
          <div className="apparel-social-links">
            {/* Social links removed to be strictly dynamic */}
          </div>
        </div>

        {/* Foreground Content Card (Left) */}
        <div className="apparel-banner__card">
          <div className="apparel-banner__card-content">
            <h1 className="apparel-banner__main-title" dangerouslySetInnerHTML={{ __html: banner.heading.replace(' ', '<br/>') }}></h1>
            <p className="apparel-banner__desc">
              {banner.description}
            </p>
            <a href="/restaurants" className="apparel-banner__btn" onClick={handleShopClick}>
              {banner.button_text}
            </a>
          </div>
        </div>

        {/* Model Cutout Image (Center-Right) */}
        <div className="apparel-banner__model-wrapper">
          <img
            src={config.backgroundImages.banner}
            alt={banner.heading}
            className="apparel-banner__model-img"
          />
        </div>

        {/* Floating Typography (Right) */}
        <div className="apparel-banner__floating-text">
          <h2 className="apparel-banner__sub-title" dangerouslySetInnerHTML={{ __html: banner.sub_heading.replace(' ', '<br/>') }}></h2>
        </div>

      </div>
    </div>
  );
};

const LuxuryBanner = ({ config, stores = [], onSingleStoreQuickFlow }) => {
  const { banner } = config;
  const navigate = useNavigate();

  const handleReserveClick = (e) => {
    e.preventDefault();
    if (stores.length === 1 && onSingleStoreQuickFlow) {
      onSingleStoreQuickFlow();
    } else {
      navigate('/restaurants');
    }
  };

  return (
    <div id="banner" className="luxury-banner">
      <div className="luxury-banner__container">
        <div className="luxury-banner__content">
          <p className="luxury-banner__sub">{banner.sub_heading}</p>
          <h1 className="luxury-banner__title">
            {banner.heading}
          </h1>
          <p className="luxury-banner__desc">{banner.description}</p>
          <a
            href="/restaurants"
            className="luxury-banner__btn"
            onClick={handleReserveClick}
          >
            {banner.button_text}
          </a>
        </div>

        <div className="luxury-banner__visuals">
          <div className="luxury-food luxury-food--1">
            <img src={config.backgroundImages.banner} alt="Featured Food 1" />
          </div>
          <div className="luxury-food luxury-food--2">
            <img src={config.backgroundImages.banner} alt="Featured Food 2" />
          </div>

        </div>
      </div>
    </div>
  );
};

const SweetShopBanner = ({ config, stores = [], onSingleStoreQuickFlow }) => {
  const { banner } = config;
  const navigate = useNavigate();

  const handleShopClick = (e) => {
    e.preventDefault();
    if (stores.length === 1 && onSingleStoreQuickFlow) {
      onSingleStoreQuickFlow();
    } else {
      navigate('/restaurants');
    }
  };

  return (
    <div id="banner" className="wow-hero">
      {/* ── Animated background blobs ── */}
      <div className="wow-hero__bg">
        <div className="wow-blob wow-blob--1" />
        <div className="wow-blob wow-blob--2" />
        <div className="wow-blob wow-blob--3" />
      </div>

      {/* ── Floating decorative particles ── */}
      <div className="wow-particles" aria-hidden="true">
        {['✦', '✦', '★', '✦', '✦', '★', '✦'].map((s, i) => (
          <span key={i} className={`wow-particle wow-particle--${i + 1}`}>{s}</span>
        ))}
      </div>

      <div className="wow-hero__inner container">

        {/* ════ LEFT ════ */}
        <div className="wow-hero__left">
          {/* Eyebrow tag */}
          <div className="wow-eyebrow">
            <span className="wow-eyebrow__dot" />&nbsp;
            {banner.sub_heading}
            &nbsp;<span className="wow-eyebrow__dot" />
          </div>

          {/* BIG headline */}
          <h1 className="wow-title">
            {banner.heading}
            <span className="wow-title__dot">.</span>
          </h1>

          {/* Gold script sub */}
          <p className="wow-script">{config.name}</p>

          {/* Description */}
          <p className="wow-desc">{banner.description}</p>

          {/* CTA Buttons */}
          <div className="wow-cta">
            <a href="/restaurants" className="wow-btn wow-btn--shine" onClick={handleShopClick}>
              <i className="fa fa-shopping-bag" />
              {banner.button_text}
            </a>
          </div>
        </div>

        {/* ════ RIGHT ════ */}
        <div className="wow-hero__right">
          {/* Main product image */}
          <div className="wow-product">
            <div className="wow-product__glow" />
            <img
              src={config.backgroundImages.banner}
              alt={banner.heading}
              className="wow-product__img"
            />
          </div>

          {/* Floating badges from trust_badges */}
        </div>
      </div>

      {/* Bottom wave */}
      <div className="wow-wave" aria-hidden="true">
        <svg viewBox="0 0 1440 80" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,30 C300,80 900,0 1440,55 L1440,80 L0,80 Z" fill="#ffffff" fillOpacity="1" />
        </svg>
      </div>
    </div>
  );
};

const HeritageBanner = ({ config, stores = [], onSingleStoreQuickFlow }) => {
  const { banner } = config;
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleOrderClick = (e) => {
    e.preventDefault();
    if (stores.length === 1 && onSingleStoreQuickFlow) {
      onSingleStoreQuickFlow();
    } else {
      navigate('/restaurants');
    }
  };

  return (
    <div id="banner" className={`hr-banner ${isVisible ? 'hr-banner--visible' : ''}`}>
      {/* Background image */}
      <div className="hr-banner__bg">
        <img src={config.backgroundImages.banner} alt={banner.heading} className="hr-banner__bg-img" />
      </div>
      {/* Dark overlay */}
      <div className="hr-banner__overlay" />
      {/* Ornate gold frame */}
      <div className="hr-banner__frame" />

      {/* Content */}
      <div className="hr-banner__content">
        {/* Gold flourish top */}
        <div className="hr-banner__flourish">
          <span className="hr-flourish-line" />
          <span className="hr-flourish-icon">❧</span>
          <span className="hr-flourish-line" />
        </div>

        <p className="hr-banner__kicker">{banner.sub_heading || ''}</p>
        <h1 className="hr-banner__title">{banner.heading}</h1>
        <p className="hr-banner__desc">{banner.description}</p>

        <div className="hr-banner__actions">
          <a href="/restaurants" className="hr-banner__btn hr-banner__btn--primary" onClick={handleOrderClick}>
            <span className="hr-banner__btn-icon">🛒</span>
            {banner.button_text}
          </a>
        </div>

        {/* Gold flourish bottom */}
        <div className="hr-banner__flourish hr-banner__flourish--bottom">
          <span className="hr-flourish-line" />
          <span className="hr-flourish-icon">❧</span>
          <span className="hr-flourish-line" />
        </div>
      </div>
    </div>
  );
};

const ModernMithaiBanner = ({ config, stores = [], onSingleStoreQuickFlow }) => {
  const { banner, backgroundImages } = config;
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  const handleOrderClick = (e) => {
    e.preventDefault();
    if (stores.length === 1 && onSingleStoreQuickFlow) {
      onSingleStoreQuickFlow();
    } else {
      navigate('/restaurants');
    }
  };

  const bgImg =
    backgroundImages?.banner ||
    banner?.image_url ||
    banner?.image ||
    banner?.slides?.[0]?.image ||
    banner?.slides?.[0]?.url ||
    '';

  return (
    <div
      id="banner"
      className={`d3-teal-deco ${isVisible ? 'd3-banner--visible' : ''}`}
    >
      {/* Full background image — no heavy gradient overlay */}
      {bgImg && (
        <div
          className="d3-bg-image"
          style={{ backgroundImage: `url("${bgImg}")` }}
        />
      )}
      {/* Soft left-side gradient for text readability */}
      <div className="d3-overlay" />

      <div className="d3-inner">
        <div className="d3-eyebrow">
          {banner?.sub_heading || banner?.subHeading || 'Pure, Fresh & Nutritious'}
        </div>
        <h1 className="d3-headline">
          {banner?.heading || banner?.mainHeading || 'Welcome'}
        </h1>
        <p className="d3-sub">
          {banner?.description ||
            'Discover our authentic flavors and culinary excellence.'}
        </p>
        <a href="/restaurants" className="d3-btn" onClick={handleOrderClick}>
          <span>{banner?.button_text || banner?.buttonText || 'Order Now'}</span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M13 5l7 7-7 7" />
          </svg>
        </a>
      </div>
    </div>
  );
};

const FestiveSweetsBanner = ({ config, stores = [], onSingleStoreQuickFlow }) => {
  const { banner } = config;
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleOrderClick = (e) => {
    e.preventDefault();
    if (stores.length === 1 && onSingleStoreQuickFlow) {
      onSingleStoreQuickFlow();
    } else {
      navigate('/restaurants');
    }
  };

  return (
    <div id="banner" className={`fs-banner ${isVisible ? 'fs-banner--visible' : ''}`}>
      {/* Decorative rangoli patterns */}
      <div className="fs-banner__rangoli fs-banner__rangoli--1" />
      <div className="fs-banner__rangoli fs-banner__rangoli--2" />
      <div className="fs-banner__rangoli fs-banner__rangoli--3" />

      {/* Diagonal clip background */}
      <div className="fs-banner__diagonal">
        <img
          src={config.backgroundImages.banner}
          alt={banner.heading}
          className="fs-banner__bg-img"
        />
        <div className="fs-banner__diagonal-overlay" />
      </div>

      <div className="fs-banner__container">
        {/* Left content */}
        <div className="fs-banner__content">
          <div className="fs-banner__badge">
            <span className="fs-banner__badge-icon">🪔</span>
            {banner.sub_heading}
          </div>

          <h1 className="fs-banner__title">
            {banner.heading}
          </h1>

          <p className="fs-banner__subtitle">{banner.sub_heading}</p>
          <p className="fs-banner__desc">{banner.description}</p>

          <div className="fs-banner__actions">
            <a
              href="/restaurants"
              className="fs-banner__btn fs-banner__btn--primary"
              onClick={handleOrderClick}
            >
              <i className="fa fa-shopping-bag" style={{ marginRight: '8px' }} />
              {banner.button_text}
            </a>
          </div>

          {/* Dynamic trust badges */}
          <div className="fs-banner__trust">
          </div>
        </div>

        {/* Right floating product circle */}
        <div className="fs-banner__visual">
          <div className="fs-banner__circle-outer" />
          <div className="fs-banner__circle-inner">
            <img
              src={config.backgroundImages.banner}
              alt={banner.heading}
              className="fs-banner__product-img"
            />
          </div>
        </div>
      </div>

      {/* Bottom wave */}
      <div className="fs-banner__wave">
        <svg viewBox="0 0 1440 80" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,50 C480,10 960,90 1440,30 L1440,80 L0,80 Z" fill="#FFFDE7" fillOpacity="1" />
        </svg>
      </div>
    </div>
  );
};

const RoyalConfectionsBanner = ({ config, stores = [], onSingleStoreQuickFlow }) => {
  const { banner } = config;
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleOrderClick = (e) => {
    e.preventDefault();
    if (stores.length === 1 && onSingleStoreQuickFlow) {
      onSingleStoreQuickFlow();
    } else {
      navigate('/restaurants');
    }
  };

  return (
    <div id="banner" className={`rc-banner ${isVisible ? 'rc-banner--visible' : ''}`}>
      {/* Full-bleed background */}
      <div className="rc-banner__bg">
        <img
          src={config.backgroundImages.banner}
          alt={banner.heading}
          className="rc-banner__bg-img"
        />
        <div className="rc-banner__bg-overlay" />
        <div className="rc-banner__bg-grain" />
      </div>

      {/* Decorative elements */}
      <div className="rc-banner__deco-line rc-banner__deco-line--1" />
      <div className="rc-banner__deco-line rc-banner__deco-line--2" />

      <div className="rc-banner__container">
        {/* Main content */}
        <div className="rc-banner__content">
          <div className="rc-banner__eyebrow">
            <span className="rc-banner__eyebrow-line" />
            <span className="rc-banner__eyebrow-text">{banner.sub_heading}</span>
            <span className="rc-banner__eyebrow-line" />
          </div>

          <h1 className="rc-banner__title">
            {banner.heading}
          </h1>

          <p className="rc-banner__desc">{banner.description}</p>

          <div className="rc-banner__actions">
            <a
              href="/restaurants"
              className="rc-banner__btn rc-banner__btn--primary"
              onClick={handleOrderClick}
            >
              {banner.button_text}
              <i className="fa fa-long-arrow-right" style={{ marginLeft: '12px' }} />
            </a>
          </div>
        </div>

        {/* Glassmorphism info card */}
        <div className="rc-banner__glass-card">
          <div className="rc-banner__glass-inner">
          </div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="rc-banner__bottom-fade" />
    </div>
  );
};

const TraditionalSweetsBanner = ({ config, stores = [], onSingleStoreQuickFlow }) => {
  const { banner } = config;
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleOrderClick = (e) => {
    e.preventDefault();
    if (stores.length === 1 && onSingleStoreQuickFlow) {
      onSingleStoreQuickFlow();
    } else {
      navigate('/restaurants');
    }
  };

  return (
    <div id="banner" className={`ts-banner ${isVisible ? 'ts-banner--visible' : ''}`}>
      <div className="ts-banner__bg">
        <img src={config.backgroundImages.banner} alt={banner.heading} className="ts-banner__bg-img" />
      </div>

      <div className="ts-banner__container">
        <div className="ts-banner__card">
          <div className="ts-banner__card-inner">
            <div className="ts-banner__badge">
              <span>{banner.sub_heading}</span>
            </div>
            <h1 className="ts-banner__title">{banner.heading}</h1>
            <p className="ts-banner__desc">{banner.description}</p>

            <div className="ts-banner__actions">
              <a href="/restaurants" className="ts-banner__btn" onClick={handleOrderClick}>
                {banner.button_text}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative floating sweets */}
      <div className="ts-banner__deco ts-banner__deco--1" />
      <div className="ts-banner__deco ts-banner__deco--2" />
    </div>
  );
};

const LegacyAshaBanner = ({ config, stores = [], onSingleStoreQuickFlow }) => {
  const { banner } = config;
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Extract slides from banner configuration
  const slides = banner.slides && banner.slides.length > 0
    ? banner.slides.map(s => s.image)
    : [config.backgroundImages?.banner || ''];

  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 150);
    return () => clearTimeout(t);
  }, []);

  // Automatic slide transitions
  useEffect(() => {
    if (slides.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const handlePrev = (e) => {
    e.preventDefault();
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleNext = (e) => {
    e.preventDefault();
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const handleShopClick = (e) => {
    e.preventDefault();
    if (stores.length === 1 && onSingleStoreQuickFlow) {
      onSingleStoreQuickFlow();
    } else {
      navigate('/restaurants');
    }
  };

  return (
    <div
      id="banner"
      className={`asha-showcase ${isVisible ? 'asha-showcase--visible' : ''}`}
    >
      {/* Immersive background slides for cross-fade */}
      {slides.map((img, idx) => (
        <div
          key={idx}
          className={`asha-showcase__bg-slide ${idx === currentSlide ? 'asha-showcase__bg-slide--active' : ''}`}
          style={{ backgroundImage: `url(${img})` }}
        />
      ))}

      {/* Soft right-side gradient so text is readable */}
      <div className="asha-showcase__overlay" />

      {/* Decorative gold corner accents */}
      <div className="asha-showcase__corner asha-showcase__corner--tl" />
      <div className="asha-showcase__corner asha-showcase__corner--br" />

      {/* Content anchored to the right */}
      <div className="asha-showcase__content">
        <span className="asha-showcase__kicker">
          {banner.sub_heading || 'Premium Quality'}
        </span>

        <h1 className="asha-showcase__title">
          {banner.heading}
        </h1>

        <div className="asha-showcase__rule" />

        <p className="asha-showcase__desc">{banner.description}</p>

        <button className="asha-showcase__btn" onClick={handleShopClick}>
          {banner.button_text}
          <i className="fa fa-long-arrow-right" style={{ marginLeft: 10 }} />
        </button>
      </div>

      {/* Left / Right nav arrows */}
      {slides.length > 1 && (
        <>
          <button
            className="asha-showcase__arrow asha-showcase__arrow--left"
            onClick={handlePrev}
            aria-label="Previous"
          >
            <i className="fa fa-chevron-left" />
          </button>
          <button
            className="asha-showcase__arrow asha-showcase__arrow--right"
            onClick={handleNext}
            aria-label="Next"
          >
            <i className="fa fa-chevron-right" />
          </button>
        </>
      )}
    </div>
  );
};

const SunburstBanner = ({ config, stores = [], onSingleStoreQuickFlow }) => {
  const { banner } = config;
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleOrderClick = (e) => {
    e.preventDefault();
    if (stores.length === 1 && onSingleStoreQuickFlow) {
      onSingleStoreQuickFlow();
    } else {
      navigate('/restaurants');
    }
  };

  return (
    <div id="banner" className={`ssb-hero ${isVisible ? 'ssb-hero--visible' : ''}`}>
      <div className="ssb-hero__rays">
        {[...Array(16)].map((_, i) => (
          <div key={i} className="ssb-hero__ray" style={{ '--i': i }}></div>
        ))}
      </div>

      <div className="container">
        <div className="ssb-hero__inner">
          <div className="ssb-hero__content">
            <div className="ssb-hero__tag">{banner.sub_heading}</div>
            <h1 className="ssb-hero__title">
              {banner.heading}
            </h1>
            <p className="ssb-hero__desc">{banner.description}</p>

            <div className="ssb-hero__badges">
            </div>

            <div className="ssb-hero__actions">
              <a href="/restaurants" className="ssb-hero__btn ssb-hero__btn--primary" onClick={handleOrderClick}>
                {banner.button_text}
                <i className="fa fa-chevron-right"></i>
              </a>
            </div>
          </div>

          <div className="ssb-hero__visual">
            <div className="ssb-hero__glow" />
            <img
              src={config.backgroundImages.banner}
              alt={banner.heading}
              className="ssb-hero__product-img"
            />

          </div>
        </div>
      </div>

      <div className="ssb-hero__wave">
        <svg viewBox="0 0 1440 80" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,30 C300,80 900,-20 1440,60 L1440,80 L0,80 Z" fill="white" />
        </svg>
      </div>
    </div>
  );
};

const CafeBanner = ({ config, stores = [], onSingleStoreQuickFlow }) => {
  const { banner } = config;
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleOrderClick = (e) => {
    e.preventDefault();
    if (stores.length === 1 && onSingleStoreQuickFlow) {
      onSingleStoreQuickFlow();
    } else {
      navigate('/restaurants');
    }
  };

  return (
    <div id="banner" className={`cafe-banner ${isVisible ? 'cafe-banner--visible' : ''}`}>
      <div className="cafe-banner__bg">
        <img
          src={config.backgroundImages.banner}
          alt={banner.heading}
          className="cafe-banner__bg-img"
        />
        <div className="cafe-banner__bg-overlay" />
      </div>

      <div className="cafe-banner__steam" aria-hidden="true">
        {[...Array(6)].map((_, i) => (
          <div key={i} className={`cafe-steam-particle cafe-steam-particle--${i + 1}`} />
        ))}
      </div>

      <div className="cafe-banner__container">
        <div className="cafe-banner__glass">
          <div className="cafe-banner__glass-inner">
            <div className="cafe-banner__eyebrow">
              <span className="cafe-banner__eyebrow-bean">☕</span>
              <span className="cafe-banner__eyebrow-text">{banner.sub_heading}</span>
            </div>

            <h1 className="cafe-banner__title">
              {banner.heading}
            </h1>

            <p className="cafe-banner__desc">{banner.description}</p>

            <div className="cafe-banner__actions">
              <a
                href="/restaurants"
                className="cafe-banner__btn cafe-banner__btn--primary"
                onClick={handleOrderClick}
              >
                <i className="fa fa-coffee" style={{ marginRight: '10px' }} />
                {banner.button_text}
              </a>
            </div>

            <div className="cafe-banner__trust">
            </div>
          </div>
        </div>

        <div className="cafe-banner__showcase">
          <div className="cafe-banner__cup-wrap">
            <div className="cafe-banner__cup-glow" />
            <img
              src={config.backgroundImages.banner}
              alt={banner.heading}
              className="cafe-banner__cup-img"
            />
          </div>
        </div>
      </div>

      <div className="cafe-banner__wave" aria-hidden="true">
        <svg viewBox="0 0 1440 80" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,45 C360,80 720,10 1440,55 L1440,80 L0,80 Z" fill="#FDF8F3" fillOpacity="1" />
        </svg>
      </div>
    </div>
  );
};

const DryFruitsBanner = ({ config, stores = [], onSingleStoreQuickFlow }) => {
  const { banner, backgroundImages } = config;
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleShopClick = (e) => {
    e.preventDefault();
    if (stores.length === 1 && onSingleStoreQuickFlow) {
      onSingleStoreQuickFlow();
    } else {
      navigate('/restaurants');
    }
  };

  const bgImg =
    backgroundImages?.banner ||
    banner?.image_url ||
    banner?.image ||
    banner?.slides?.[0]?.image ||
    banner?.slides?.[0]?.url ||
    '';

  return (
    <div
      id="banner"
      className={`df-banner d11-festive-hamper ${isVisible ? 'df-banner--visible' : ''}`}
      style={
        bgImg
          ? { backgroundImage: `linear-gradient(180deg, rgba(46,10,14,.72) 0%, rgba(46,10,14,.55) 45%, rgba(46,10,14,.8) 100%), url("${bgImg}")` }
          : {}
      }
    >
      <div className="d11-frame">
        <div className="d11-corner tl" />
        <div className="d11-corner tr" />
        <div className="d11-corner bl" />
        <div className="d11-corner br" />
        <div className="d11-eyebrow">
          {banner?.sub_heading || banner?.subHeading || 'Pure, Fresh & Nutritious'}
        </div>
        <h1 className="d11-headline">
          {banner?.heading || banner?.mainHeading || 'Fresh Dry Fruits for a Healthier You'}
        </h1>
        <div className="d11-rule" />
        <p className="d11-sub">
          {banner?.description ||
            'Experience the finest selection of handpicked dry fruits, nuts, and seeds—carefully sourced, hygienically packed, and delivered fresh to your doorstep.'}
        </p>
        <a href="/restaurants" className="d11-btn" onClick={handleShopClick}>
          <span>{banner?.button_text || banner?.buttonText || 'Order Now'}</span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M13 5l7 7-7 7" />
          </svg>
        </a>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// TEMPLATE 16 — ARISA COLLECTION (Premium Sweet Store)
// Full-bleed editorial hero: generous negative space, muted wine/gold
// palette, thin rule motif instead of ornate borders.
// ═══════════════════════════════════════════════════════════════════
const SweetStoreBanner = ({ config, stores = [], onSingleStoreQuickFlow }) => {
  const { banner, backgroundImages } = config;
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const touchStartX = useRef(null);

  const fallbackImg =
    backgroundImages?.banner ||
    banner?.image_url ||
    banner?.image ||
    '';

  // Real slides only — never fabricate extra ones, and never show a
  // placeholder. If a slide (or the single fallback banner) has no real
  // image, it's dropped rather than rendered as an empty box.
  const rawSlides = Array.isArray(banner?.slides) ? banner.slides.filter(s => s && (s.image || s.image_url || s.url)) : [];
  const slides = (rawSlides.length > 0
    ? rawSlides.map(s => ({
      image: s.image || s.image_url || s.url,
      // Optional: a portrait/tall image made specifically for phones. Not
      // part of the current content schema — only used if a slide actually
      // has one, so nothing changes until that field is populated.
      mobileImage: s.mobile_image || s.mobile_image_url || null,
      buttonLink: s.button_link || s.buttonLink || banner?.button_link || banner?.buttonLink,
      heading: s.heading || s.mainHeading || banner?.heading || banner?.mainHeading
    }))
    : (fallbackImg ? [{
      image: fallbackImg,
      mobileImage: banner?.mobile_image || banner?.mobile_image_url || null,
      buttonLink: banner?.button_link || banner?.buttonLink,
      heading: banner?.heading || banner?.mainHeading
    }] : [])
  );

  const goToSlide = (idx) => {
    if (idx === activeIndex || slides.length <= 1) return;
    setFade(false);
    setTimeout(() => {
      setActiveIndex(idx);
      setFade(true);
    }, 280);
  };

  // Auto-advance — only when there's genuinely more than one real slide.
  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const interval = setInterval(() => {
      goToSlide((activeIndex + 1) % slides.length);
    }, 6000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, slides.length]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null || slides.length <= 1) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(deltaX) > 45) {
      if (deltaX < 0) goToSlide((activeIndex + 1) % slides.length);
      else goToSlide((activeIndex - 1 + slides.length) % slides.length);
    }
    touchStartX.current = null;
  };

  // No real banner image anywhere — nothing honest to show, so skip the
  // section entirely rather than render an empty/placeholder box.
  if (slides.length === 0) return null;

  const currentSlide = slides[activeIndex] || slides[0];
  const currentLink = currentSlide.buttonLink || '/restaurants';

  const handleShopClick = (e) => {
    e.preventDefault();
    if (stores.length === 1 && onSingleStoreQuickFlow) {
      onSingleStoreQuickFlow();
    } else {
      navigate(currentLink);
    }
  };

  return (
    <div
      id="banner"
      className="t16-hero t16-hero--visible"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <a
        href={currentLink}
        className={`t16-hero__slide ${fade ? 't16-hero__slide--fade-in' : 't16-hero__slide--fade-out'} ${currentSlide.mobileImage ? 't16-hero__slide--has-mobile-img' : ''}`}
        onClick={handleShopClick}
      >
        <picture>
          {currentSlide.mobileImage && (
            <source media="(max-width: 576px)" srcSet={currentSlide.mobileImage} />
          )}
          <img
            src={currentSlide.image}
            alt={currentSlide.heading || 'Sweets'}
            className="t16-hero__slide-img"
          />
        </picture>
      </a>

      {slides.length > 1 && (
        <div className="t16-hero__dots">
          {slides.map((_, idx) => (
            <button
              key={idx}
              type="button"
              className={`t16-hero__dot ${idx === activeIndex ? 't16-hero__dot--active' : ''}`}
              aria-label={`Go to slide ${idx + 1}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); goToSlide(idx); }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Banner = (props) => {
  const type = (props.config.templateType || 'template1').toLowerCase().replace(/-/g, '_');

  switch (type) {
    case 'template1':
    case 'modern':
      return <ModernBanner {...props} />;
    case 'template2':
    case 'classic':
      return <ClassicBanner {...props} />;
    case 'template3':
    case 'traditional':
      return <TraditionalBanner {...props} />;
    case 'template4':
    case 'asha_legacy':
      return <LegacyAshaBanner {...props} />;
    case 'template5':
    case 'apparel':
      return <ApparelBanner {...props} />;
    case 'template6':
    case 'luxury_restaurant':
      return <LuxuryBanner {...props} />;
    case 'template7':
    case 'sweet_shop':
      return <SweetShopBanner {...props} />;
    case 'template8':
    case 'heritage_royal':
      return <HeritageBanner {...props} />;
    case 'template9':
    case 'modern_mithai':
      return <ModernMithaiBanner {...props} />;
    case 'template10':
    case 'festive_sweets':
      return <FestiveSweetsBanner {...props} />;
    case 'template11':
    case 'royal_confections':
      return <RoyalConfectionsBanner {...props} />;
    case 'template12':
    case 'traditional_sweets':
      return <TraditionalSweetsBanner {...props} />;
    case 'template13':
    case 'sunburst_gourmet':
      return <SunburstBanner {...props} />;
    case 'template14':
    case 'cafe_premium':
      return <CafeBanner {...props} />;
    case 'template15':
    case 'dry_fruits':
      return <DryFruitsBanner {...props} />;
    case 'template16':
    case 'arisa_collection':
      return <SweetStoreBanner {...props} />;
    default:
      return <ModernBanner {...props} />;
  }
};

export default Banner;
